import {
  ApiTags,
  SubGroup,
  ApiGeneralDataResponse,
  ApiResponse,
  CancelSignalRequest,
  ConfigurationItem,
  EditSignalLimits,
  EditSignalRequest,
  EditSignalResponse,
  GetExchangeSignalByIdRequest,
  GetSignalByIdRequest,
  GetSignalsRequest,
  GetSignalsResponse,
  GetSignalsSubGroupsResponse,
  OneClickFollowSignalRequest,
  OneClickFollowSignalResponse,
  ProcessedSignalTargets,
  RawUnZippedSignalTargets,
  Signal,
  SignalExchange,
  SignalExtraDataResponse,
  SignalGeneralExtraData,
  TargetCardData,
  unZippedTargetCard,
  SignalExtraDetailsConfiguration,
  TradeExtraInfoActivity,
} from "@shared/types";
import { convertToObjectsArray } from "@shared/utils/arrays";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import {
  getSignalsFiltersSlice,
  processSignalGeneralData,
} from "@shared/features/signals/common/utils";
import { zipSignals } from "@shared/features/signals/common/utils";
import { getJobQuery } from "@shared/services/jobs/utils";

export const signalsEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => {
    return {
      getSignalsData: build.query<GetSignalsResponse, GetSignalsRequest>({
        query: (signalRequest) => ({
          url: "/get_signals_data/",
          method: "POST",
          body: signalRequest,
        }),
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const filters = getSignalsFiltersSlice(
              args.is_open,
              args.for_admin
            );
            dispatch(
              filters.actions.loadFilters({ data, onlyAddOptions: true })
            );
          } catch {}
        },
        transformResponse: (
          response: ApiResponse<
            ApiGeneralDataResponse<Record<string, SignalExchange>>
          >,
          _,
          args
        ) => {
          const exchangesMap = response?.data?.data;
          let signals: Signal[] = [];
          if (exchangesMap) {
            signals = zipSignals(response, args);
          }
          return {
            signals,
            filters: response.data?.filters,
            precision: response.data?.precision,
          };
        },
        providesTags: [{ type: ApiTags.SIGNALS, id: "LIST" }],
      }),
      getSignalsSubGroups: build.query<SubGroup[], void>({
        query: () => ({
          url: "/get_signals_sub_groups/",
          method: "POST",
        }),
        transformResponse: (
          response: ApiResponse<GetSignalsSubGroupsResponse>
        ) => convertToObjectsArray(response.data.data, response.data.headers),
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const openSignalsFilters = getSignalsFiltersSlice(true);
            const closedSignalsFilters = getSignalsFiltersSlice(false);
            dispatch(
              openSignalsFilters.actions.loadGroups({ data: data || [] })
            );
            dispatch(
              closedSignalsFilters.actions.loadGroups({ data: data || [] })
            );
          } catch {}
        },
        providesTags: () => [{ type: ApiTags.SIGNALS_SUB_GROUPS, id: "LIST" }],
      }),
      getSignalExtraInfoGeneral: build.query<
        SignalGeneralExtraData,
        GetSignalByIdRequest
      >({
        query: (signalRequest) => ({
          url: "/get_signal_extra_info_general/",
          method: "POST",
          body: signalRequest,
        }),
        transformResponse: (
          response: ApiResponse<{
            data: SignalExtraDataResponse;
          }>
        ) => processSignalGeneralData(response.data.data),
        providesTags: (_, __, args) => [
          { type: ApiTags.SIGNALS, id: args.signal_id },
        ],
        keepUnusedDataFor: 0,
      }),
      getSignalExtraInfoActivities: build.query<
        TradeExtraInfoActivity[],
        GetExchangeSignalByIdRequest
      >({
        query: (signalRequest) => ({
          url: "/get_exchange_signal_extra_info_activities/",
          method: "POST",
          body: signalRequest,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<(string | number)[]>>
        ) => {
          const signalActivitiesResponse = response.data;
          return convertToObjectsArray(
            signalActivitiesResponse.data,
            signalActivitiesResponse.headers
          );
        },
        keepUnusedDataFor: 0,
      }),
      getSignalExtraInfoTargets: build.query<
        ApiGeneralDataResponse<ProcessedSignalTargets, EditSignalLimits>,
        GetExchangeSignalByIdRequest
      >({
        query: (signalRequest) => ({
          url: "/get_exchange_signal_extra_info_targets/",
          method: "POST",
          body: signalRequest,
        }),
        transformResponse(
          response: ApiResponse<
            ApiGeneralDataResponse<RawUnZippedSignalTargets, EditSignalLimits>
          >
        ) {
          const getTargetCards = (
            rawTargets: unZippedTargetCard[],
            headers?: string[]
          ): TargetCardData[] => {
            return rawTargets && headers
              ? convertToObjectsArray(rawTargets, headers)
              : [];
          };
          const headers = response.data.headers;
          const rawSignalExtraInfoTargetsData = response.data.data;
          const processedData = {
            entry_target_cards: getTargetCards(
              rawSignalExtraInfoTargetsData.entry_target_cards,
              headers
            ),
            take_profit_target_cards: getTargetCards(
              rawSignalExtraInfoTargetsData.take_profit_target_cards,
              headers
            ),
            stop_target_cards: getTargetCards(
              rawSignalExtraInfoTargetsData.stop_target_cards,
              headers
            ),
          };

          return {
            ...response.data,
            data: {
              ...response.data.data,
              ...processedData,
              limits: response.data.limits,
            },
          };
        },
        keepUnusedDataFor: 0,
      }),
      getSignalExtraInfoConfiguration: build.query<
        ApiGeneralDataResponse<SignalExtraDetailsConfiguration>,
        GetSignalByIdRequest
      >({
        query: (signalRequest) => ({
          url: "/get_signal_extra_info_configuration/",
          method: "POST",
          body: signalRequest,
        }),
        transformResponse: (
          response: ApiResponse<
            ApiGeneralDataResponse<SignalExtraDetailsConfiguration>
          >
        ) => response.data,
        providesTags: (_, __, args) => [
          { type: ApiTags.SIGNALS, id: args.signal_id },
        ],
        keepUnusedDataFor: 0,
      }),
      getMostFulfilledSignalForGroup: build.query<
        { exchange_signal_id: number },
        GetSignalByIdRequest
      >({
        query: (args) => ({
          url: "/get_most_fulfilled_exchange_signal/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<number>) => {
          return {
            exchange_signal_id: response.data,
          };
        },
        keepUnusedDataFor: 0,
      }),
      getExchangeSignalIds: build.query<
        Record<string, number>,
        GetSignalByIdRequest
      >({
        query: (args) => ({
          url: "/get_exchange_signal_ids/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<Record<string, number>>) => {
          return response.data;
        },
        keepUnusedDataFor: 0,
      }),
      editSignal: build.mutation<EditSignalResponse, EditSignalRequest>({
        query: (args) => ({
          url: "edit_signal/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<number>) => {
          return { new_signal_id: response.data };
        },
        invalidatesTags: (response) =>
          response ? [{ type: ApiTags.SIGNALS, id: "LIST" }] : [],
      }),
      editSignalConfiguration: build.mutation<
        void,
        { configuration: ConfigurationItem; signal_id: number }
      >({
        query: (args) => ({
          url: "edit_signal_configuration/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response, _, args) =>
          response ? [{ type: ApiTags.SIGNALS, id: args.signal_id }] : [],
      }),
      cancelSignal: build.mutation<void, CancelSignalRequest>({
        query: (args) => ({
          url: "cancel_signal/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response, _, args) =>
          response
            ? [
                { type: ApiTags.SIGNALS, id: args.signal_id },
                { type: ApiTags.SIGNALS, id: "LIST" },
              ]
            : [],
      }),
      oneClickFollowSignal: build.mutation<
        OneClickFollowSignalResponse,
        OneClickFollowSignalRequest
      >({
        query: (args) => ({
          url: "one_click_follow_signal/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<number>) => {
          return { new_trade_id: response.data };
        },
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.SIGNALS, id: "LIST" },
                { type: ApiTags.TRADES, id: "LIST" },
              ]
            : [],
      }),
      exportSignals: getJobQuery({
        build,
        endpointName: "exportSignalsJob",
        transformResponse: (response) => {
          return response?.data?.path;
        },
      }),
    };
  },
  overrideExisting: false,
};
